/**
 * STT Stream Processor
 *
 * Processes audio stream through Speech-to-Text using Silero VAD.
 * Silero VAD is ML-based and provides speech audio segments directly at 16kHz.
 *
 * Flow:
 * Audio Stream → Silero VAD (ML) → onSpeechEnd(audio@16kHz) → Whisper → Transcription
 *
 * Benefits of Silero VAD:
 * - ML-based, ~99% accuracy (vs ~70% energy-based)
 * - Provides speech audio directly at 16kHz (no resampling needed)
 * - Handles buffering internally
 * - MIT license, commercially safe
 */

import { getOptimalSTTProvider } from './audio/stt/index.js';
import { SileroVADProvider } from './audio/vad/SileroVADProvider.js';

// Singleton state
let isProcessing = false;
let sttProvider = null;
let vadProvider = null;
let utteranceId = 0;

// Configuration
let currentLanguage = 'en';

// Callbacks
let onTranscriptionCallback = null;
let onLoadingProgressCallback = null;
let onSpeechStartCallback = null;
let onSpeechEndCallback = null;
let onReadyCallback = null;
let onActiveSpeakerCallback = null;

/**
 * Start STT processing on an audio stream
 *
 * @param {MediaStream} inputStream - The audio stream (ideally noise-suppressed)
 * @param {Object} options - Configuration options
 * @param {string} options.language - Language code for transcription
 * @param {Function} options.onTranscription - Called with transcription result
 * @param {Function} options.onLoadingProgress - Called during model loading
 * @param {Function} options.onSpeechStart - Called when speech starts
 * @param {Function} options.onSpeechEnd - Called when speech ends
 * @param {Function} options.onReady - Called when STT is ready
 * @param {Function} options.onActiveSpeaker - Called to update active speaker (for video border)
 */
export async function startSTTProcessing(inputStream, options = {}) {
  if (isProcessing) {
    console.log('[STTStreamProcessor] Already processing');
    return;
  }

  console.log('[STTStreamProcessor] Starting STT processing with Silero VAD...');

  // Store callbacks
  currentLanguage = options.language || 'en';
  onTranscriptionCallback = options.onTranscription || null;
  onLoadingProgressCallback = options.onLoadingProgress || null;
  onSpeechStartCallback = options.onSpeechStart || null;
  onSpeechEndCallback = options.onSpeechEnd || null;
  onReadyCallback = options.onReady || null;
  onActiveSpeakerCallback = options.onActiveSpeaker || null;

  try {
    // ============================================
    // STEP 1: Initialize Silero VAD
    // ML-based VAD, much more accurate than energy-based
    // ============================================
    console.log('[STTStreamProcessor] Initializing Silero VAD...');
    vadProvider = new SileroVADProvider();

    // Set up VAD callbacks BEFORE initialization
    vadProvider.onSpeechStart = () => {
      console.log('[STTStreamProcessor] Silero VAD: Speech started');
      utteranceId++;
      if (onSpeechStartCallback) onSpeechStartCallback();
      if (onActiveSpeakerCallback) onActiveSpeakerCallback();
    };

    vadProvider.onSpeechEnd = async (audio) => {
      console.log('[STTStreamProcessor] Silero VAD: Speech ended, audio length:', audio?.length);
      if (onSpeechEndCallback) onSpeechEndCallback();

      // Transcribe the speech audio (already at 16kHz from Silero)
      if (audio && audio.length > 0 && sttProvider?.isReady()) {
        await transcribeAudio(audio);
      } else if (audio && audio.length > 0) {
        console.log('[STTStreamProcessor] STT not ready yet, skipping transcription');
      }
    };

    vadProvider.onVADMisfire = () => {
      console.log('[STTStreamProcessor] Silero VAD: Misfire (speech too short)');
    };

    // Validate input stream
    const audioTracks = inputStream?.getAudioTracks() || [];
    const hasValidStream = audioTracks.length > 0 && audioTracks[0].readyState === 'live';

    // Initialize Silero VAD with provided stream (noise-suppressed from RNNoise)
    // If no valid stream, MicVAD will request its own microphone access
    const vadInitOptions = {};
    if (hasValidStream) {
      console.log('[STTStreamProcessor] Using provided stream for VAD (RNNoise processed)');
      console.log('[STTStreamProcessor] Stream track:', audioTracks[0].label, 'state:', audioTracks[0].readyState);
      vadInitOptions.stream = inputStream;
    } else {
      console.log('[STTStreamProcessor] No valid stream provided, VAD will request microphone');
    }

    await vadProvider.initialize(vadInitOptions);

    // Start VAD processing
    await vadProvider.start();
    console.log('[STTStreamProcessor] Silero VAD started');

    // Notify VAD is ready
    if (onReadyCallback) {
      onReadyCallback({
        vadReady: true,
        sttReady: false,
        provider: 'loading',
        language: currentLanguage,
      });
    }

    isProcessing = true;

    // ============================================
    // STEP 2: Initialize STT in BACKGROUND
    // Model download happens async, doesn't block VAD
    // ============================================
    const { provider, providerName, reason } = getOptimalSTTProvider(currentLanguage);
    sttProvider = provider;
    console.log(`[STTStreamProcessor] Using ${providerName}: ${reason}`);

    // Set up loading progress callback
    sttProvider.onLoadingProgress = (progress, status) => {
      console.log(`[STTStreamProcessor] Loading model: ${progress}% - ${status}`);
      if (onLoadingProgressCallback) {
        onLoadingProgressCallback(progress, status);
      }
    };

    // Initialize STT provider in background
    console.log('[STTStreamProcessor] Initializing STT model in background...');
    sttProvider.initialize({ language: currentLanguage })
      .then(() => {
        console.log('[STTStreamProcessor] STT model ready');
        if (onReadyCallback) {
          onReadyCallback({
            vadReady: true,
            sttReady: true,
            provider: sttProvider.getName(),
            language: currentLanguage,
          });
        }
      })
      .catch((err) => {
        console.error('[STTStreamProcessor] STT model initialization failed:', err);
      });

    console.log('[STTStreamProcessor] Processing started');

  } catch (error) {
    console.error('[STTStreamProcessor] Failed to start:', error);
    stopSTTProcessing();
    throw error;
  }
}

/**
 * Transcribe audio from Silero VAD
 * Audio is already at 16kHz, no resampling needed!
 */
async function transcribeAudio(audio) {
  if (!audio || audio.length === 0) {
    console.log('[STTStreamProcessor] No audio to transcribe');
    return;
  }

  try {
    const durationSeconds = audio.length / 16000; // Silero outputs 16kHz

    // DEBUG: Analyze audio characteristics
    let peak = 0;
    let sumSquares = 0;
    let nonZeroCount = 0;
    for (let i = 0; i < audio.length; i++) {
      const val = audio[i];
      const absVal = Math.abs(val);
      if (absVal > peak) peak = absVal;
      sumSquares += val * val;
      if (absVal > 0.001) nonZeroCount++;
    }
    const rms = Math.sqrt(sumSquares / audio.length);
    const nonZeroPercent = (nonZeroCount / audio.length * 100).toFixed(1);

    console.log(`[STTStreamProcessor] Transcribing ${durationSeconds.toFixed(2)}s of audio`);
    console.log(`[STTStreamProcessor] Audio stats: RMS=${rms.toFixed(4)}, peak=${peak.toFixed(4)}, nonZero=${nonZeroPercent}%`);

    // Skip very short audio (less than 0.3s)
    if (durationSeconds < 0.3) {
      console.log('[STTStreamProcessor] Audio too short, skipping');
      return;
    }

    // Check audio energy - skip if too quiet
    const MIN_AUDIO_ENERGY = 0.005; // Lowered threshold
    if (rms < MIN_AUDIO_ENERGY) {
      console.log(`[STTStreamProcessor] Audio too quiet (energy: ${rms.toFixed(4)}), skipping`);
      return;
    }

    // Check if audio is mostly zeros (indicates silent/broken stream)
    if (nonZeroCount < audio.length * 0.1) {
      console.log(`[STTStreamProcessor] Audio mostly silent (${nonZeroPercent}% non-zero), skipping`);
      return;
    }

    // Validate and clean audio frames (fix for ">> >>" hallucination)
    // See: https://github.com/xenova/transformers.js/issues/881
    let validAudio = audio;
    let invalidCount = 0;
    for (let i = 0; i < audio.length; i++) {
      const val = audio[i];
      // Check for invalid values: NaN, Infinity, or values outside reasonable range
      if (!Number.isFinite(val) || val < -1.0 || val > 1.0) {
        if (invalidCount === 0) {
          // Create a copy only if we find invalid values
          validAudio = new Float32Array(audio);
        }
        validAudio[i] = 0; // Replace invalid with silence
        invalidCount++;
      }
    }
    if (invalidCount > 100) { // Only warn if many invalid frames
      console.warn(`[STTStreamProcessor] Fixed ${invalidCount} invalid audio frames`);
    }

    // Normalize audio to improve Whisper accuracy
    // Find the peak amplitude and scale to use more of the -1.0 to 1.0 range
    const targetPeak = 0.9;
    const gain = peak > 0.001 ? targetPeak / peak : 1;

    // Always normalize for better Whisper performance
    let normalizedAudio = new Float32Array(validAudio.length);
    for (let i = 0; i < validAudio.length; i++) {
      normalizedAudio[i] = validAudio[i] * gain;
    }
    console.log(`[STTStreamProcessor] Normalized audio: gain=${gain.toFixed(2)}x, newPeak=${(peak * gain).toFixed(4)}`)

    // Transcribe - audio is already at 16kHz!
    const currentUtteranceId = utteranceId;

    const result = await sttProvider.transcribe(normalizedAudio, {
      language: currentLanguage,
    });

    console.log(`[STTStreamProcessor] Transcription: "${result.text}"`);

    // Filter out hallucinations
    const cleanedText = filterHallucinations(result.text);

    // Send result via callback (only if there's meaningful text)
    if (onTranscriptionCallback && cleanedText.trim() && cleanedText.length > 2) {
      onTranscriptionCallback({
        text: cleanedText.trim(),
        utteranceId: currentUtteranceId,
        language: currentLanguage,
        duration: durationSeconds,
        processingTime: result.processingTime,
        rtf: result.rtf,
      });
    } else if (result.text.trim() !== cleanedText.trim()) {
      console.log(`[STTStreamProcessor] Filtered hallucination: "${result.text.substring(0, 50)}..."`);
    }

  } catch (error) {
    console.error('[STTStreamProcessor] Transcription error:', error);
  }
}

/**
 * Calculate RMS (Root Mean Square) energy of audio
 */
function calculateRMS(audioData) {
  let sum = 0;
  for (let i = 0; i < audioData.length; i++) {
    sum += audioData[i] * audioData[i];
  }
  return Math.sqrt(sum / audioData.length);
}

/**
 * Filter out Whisper hallucinations (repeated patterns)
 */
function filterHallucinations(text) {
  if (!text) return '';

  const hallucinationPatterns = [
    /^[>\s]+$/,
    /^[.\s]+$/,
    /^[\-\s]+$/,
    /^\s*\(.*\)\s*$/,
    /^\s*\[.*\]\s*$/,
    /^(.{1,3})\1{5,}$/,
    /^(\s*>+\s*)+$/,
    /^\s*♪.*♪\s*$/,
    /^(you|the|and|to|a|of|that|it|is|in|i|for)\s*$/i,
  ];

  for (const pattern of hallucinationPatterns) {
    if (pattern.test(text.trim())) {
      return '';
    }
  }

  return text
    .replace(/^[>\-\.\s]+/, '')
    .replace(/[>\-\.\s]+$/, '')
    .trim();
}

/**
 * Stop STT processing and clean up resources
 */
export function stopSTTProcessing() {
  console.log('[STTStreamProcessor] Stopping...');

  isProcessing = false;

  // Destroy Silero VAD
  if (vadProvider) {
    vadProvider.destroy().catch(e => console.warn('[STTStreamProcessor] VAD destroy error:', e));
    vadProvider = null;
  }

  // Destroy STT provider
  if (sttProvider) {
    sttProvider.destroy().catch(e => console.warn('[STTStreamProcessor] STT destroy error:', e));
    sttProvider = null;
  }

  // Clear callbacks
  onTranscriptionCallback = null;
  onLoadingProgressCallback = null;
  onSpeechStartCallback = null;
  onSpeechEndCallback = null;
  onReadyCallback = null;
  onActiveSpeakerCallback = null;

  console.log('[STTStreamProcessor] Stopped');
}

/**
 * Check if audio processing is active (VAD running)
 */
export function isProcessingActive() {
  return isProcessing;
}

/**
 * Check if VAD is ready
 */
export function isVADReady() {
  return vadProvider?.initialized || false;
}

/**
 * Check if STT is ready for transcription
 */
export function isSTTReady() {
  return sttProvider?.isReady() || false;
}

/**
 * Check if STT processing is fully active (VAD + STT ready)
 */
export function isSTTProcessingActive() {
  return isProcessing && sttProvider?.isReady();
}

/**
 * Check if STT model is loading
 */
export function isSTTLoading() {
  return sttProvider?.isLoading() || false;
}

/**
 * Get STT loading progress
 */
export function getSTTLoadingProgress() {
  return sttProvider?.getLoadingProgress() || 0;
}

/**
 * Get current STT provider name
 */
export function getSTTProviderName() {
  return sttProvider?.getName() || null;
}

/**
 * Change transcription language
 */
export async function setSTTLanguage(language) {
  if (language === currentLanguage) return;

  console.log(`[STTStreamProcessor] Changing language from ${currentLanguage} to ${language}`);
  currentLanguage = language;

  if (sttProvider) {
    sttProvider.setLanguage(language);
  }
}

export default {
  startSTTProcessing,
  stopSTTProcessing,
  isProcessingActive,
  isVADReady,
  isSTTReady,
  isSTTProcessingActive,
  isSTTLoading,
  getSTTLoadingProgress,
  getSTTProviderName,
  setSTTLanguage,
};
